import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Panel, PanelSection, toast } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { ArchivableGuard, ArchiveToggleButton } from '@/shared/archival';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import {
  getGetVenuaPlannerProjectQueryKey,
  useArchiveVenuaPlannerProject,
  useGetVenuaPlannerProject,
  useUnarchiveVenuaPlannerProject,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { plannerQueryKeys } from '../../plannerApi';
import { DestinationSelectorField } from '@/modules/planner/components/DestinationSelector';
import { PersonSelectorField } from '@/modules/planner/components/PersonSelector';
import { projectFormAgenda } from './projectFormAgenda';
import { RESPONSIBILITY_ROLE_FIELDS } from '@/modules/planner/responsibleRoles';
import { useProjectForm } from './useProjectForm';
import {
  emptyProjectFormValues,
  fromGetProjectResponse,
  type ProjectFormValues,
} from './projectFormSchema';

interface ProjectFormPageProps {
  layerId: string;
}

/**
 * Thin loader: vyřeší parametr agendy a (v editaci) načtení detailu projektu. Formulářovou část
 * vykreslí až s finálními výchozími hodnotami, takže `useForm` dostane správné defaulty hned
 * (žádný `form.reset`).
 */
export function ProjectFormPage({ layerId }: ProjectFormPageProps) {
  const { t } = useTranslation('planner');
  const { open } = useAgendaParams(projectFormAgenda, layerId);
  const projectId = open.projectId;
  const isEdit = Boolean(projectId);

  const projectQuery = useGetVenuaPlannerProject(projectId ?? '', {
    query: { enabled: isEdit },
  });

  return (
    <div className="bg-background h-full min-h-0 overflow-y-auto" data-testid="project-form">
      <div className="mx-auto w-full max-w-220 p-4">
        {isEdit && projectQuery.isLoading ? (
          <p className="text-phx-text-muted text-phx-sm">{t('project.loading')}</p>
        ) : isEdit && projectQuery.isError ? (
          <Alert severity="error" title={t('project.loadErrorTitle')}>
            {t('project.loadErrorDescription')}
          </Alert>
        ) : (
          <ProjectFormView
            layerId={layerId}
            projectId={projectId}
            initialValues={
              projectQuery.data
                ? fromGetProjectResponse(projectQuery.data)
                : emptyProjectFormValues()
            }
            isArchived={projectQuery.data?.isArchived ?? false}
          />
        )}
      </div>
    </div>
  );
}

interface ProjectFormViewProps {
  layerId: string;
  projectId?: string;
  initialValues: ProjectFormValues;
  isArchived: boolean;
}

/**
 * Vlastní formulář projektu nad TanStack Form. Archivace je samostatná akce (mimo submit formuláře).
 */
function ProjectFormView({ layerId, projectId, initialValues, isArchived }: ProjectFormViewProps) {
  const { t } = useTranslation('planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const isEdit = Boolean(projectId);

  const closeForm = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form } = useProjectForm({
    projectId,
    initialValues,
    onSaved: () => {
      void queryClient.invalidateQueries({ queryKey: plannerQueryKeys.tree() });
      toast({ severity: 'success', description: t('project.toast.saved') });
      closeForm();
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('project.toast.errorTitle'),
        description: t('project.toast.saveError'),
      });
    },
  });

  const archiveMutation = useArchiveVenuaPlannerProject();
  const unarchiveMutation = useUnarchiveVenuaPlannerProject();
  const archivalBusy = archiveMutation.isPending || unarchiveMutation.isPending;

  const invalidateAfterArchival = useCallback(() => {
    if (!projectId) return;
    void queryClient.invalidateQueries({ queryKey: getGetVenuaPlannerProjectQueryKey(projectId) });
    void queryClient.invalidateQueries({ queryKey: plannerQueryKeys.tree() });
  }, [queryClient, projectId]);

  const archivalError = useCallback(() => {
    toast({
      severity: 'error',
      title: t('project.toast.errorTitle'),
      description: t('project.toast.saveError'),
    });
  }, [t]);

  const handleArchive = useCallback(() => {
    if (!projectId) return;
    archiveMutation.mutate(
      { projectId },
      {
        onSuccess: () => {
          invalidateAfterArchival();
          toast({ severity: 'success', description: t('project.toast.archived') });
        },
        onError: archivalError,
      }
    );
  }, [projectId, archiveMutation, invalidateAfterArchival, archivalError, t]);

  const handleUnarchive = useCallback(() => {
    if (!projectId) return;
    unarchiveMutation.mutate(
      { projectId },
      {
        onSuccess: () => {
          invalidateAfterArchival();
          toast({ severity: 'success', description: t('project.toast.unarchived') });
        },
        onError: archivalError,
      }
    );
  }, [projectId, unarchiveMutation, invalidateAfterArchival, archivalError, t]);

  const title = isEdit ? t('project.titleEdit') : t('project.titleCreate');

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <Panel
        variant="filled"
        title={title}
        actions={
          isEdit ? (
            <ArchiveToggleButton
              isArchived={isArchived}
              onArchive={handleArchive}
              onUnarchive={handleUnarchive}
              disabled={archivalBusy}
            />
          ) : undefined
        }
        footer={
          <div className="gap-phx-2 flex w-full items-center justify-end">
            <form.Subscribe
              selector={(state) => ({
                canSubmit: state.canSubmit,
                isSubmitting: state.isSubmitting,
              })}
            >
              {({ canSubmit, isSubmitting }) => (
                <>
                  {!canSubmit ? (
                    <span className="text-phx-text-muted text-phx-xs mr-auto">
                      {t('project.requiredHint')}
                    </span>
                  ) : null}
                  <Button variant="ghost" size="md" type="button" onClick={closeForm}>
                    {t('project.actions.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    type="submit"
                    loading={isSubmitting}
                    disabled={isArchived || !canSubmit || isSubmitting}
                    data-testid="project-save-button"
                  >
                    {t('project.actions.save')}
                  </Button>
                </>
              )}
            </form.Subscribe>
          </div>
        }
      >
        <ArchivableGuard
          isArchived={isArchived}
          bannerDescription={t('project.archivedBannerDescription')}
        >
          <PanelSection>
            <form.Field name="name">
              {(field) => (
                <TextField
                  field={field}
                  label={t('project.name.label')}
                  required
                  helperText={t('project.name.help')}
                  placeholder={t('project.name.placeholder')}
                  data-testid="project-name-input"
                />
              )}
            </form.Field>
          </PanelSection>

          <PanelSection>
            <form.Field name="destinations">
              {(field) => (
                <DestinationSelectorField
                  field={field}
                  mode="multi"
                  label={t('project.destinations.label')}
                  helperText={t('project.destinations.help')}
                  placeholder={t('project.destinations.placeholder')}
                  testId="project-destinations"
                />
              )}
            </form.Field>
          </PanelSection>

          <PanelSection title={t('project.persons.sectionLabel')}>
            <p className="text-phx-text-muted text-phx-xs">{t('project.persons.help')}</p>
            <div className="gap-x-phx-4 gap-y-phx-3 grid grid-cols-1 md:grid-cols-2">
              {RESPONSIBILITY_ROLE_FIELDS.map((roleField) => (
                <form.Field key={roleField.role} name={`responsiblePersons.${roleField.key}`}>
                  {(field) => (
                    <PersonSelectorField
                      field={field}
                      mode="single"
                      label={t(`project.roles.${roleField.key}`)}
                      placeholder={t('project.persons.placeholder')}
                      testId={`project-person-${roleField.key}`}
                    />
                  )}
                </form.Field>
              ))}
            </div>
          </PanelSection>
        </ArchivableGuard>
      </Panel>
    </form>
  );
}
